import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import {
  toApiErrorPayload,
  type ApiErrorPayload,
} from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

export type DocumentDefinition = {
  document_type: string;
  display_name: string;
  is_required: boolean;
  is_enabled: boolean;
  is_default: boolean;
  order: number;
};

export type DocumentsMap = {
  corporate: DocumentDefinition[];
  individual: DocumentDefinition[];
};

/** 物件書類「その他」の枠定義（クライアント単位・全物件共通。ラベルのみ） */
export type PropertyDocumentDefinition = {
  document_type: string;
  display_name: string;
  order: number;
};

export type DocumentManagementRecord = {
  document_management_id: string;
  client_id: string;
  documents: DocumentsMap;
  property_other_documents?: PropertyDocumentDefinition[];
  created_at: string;
  updated_at?: string;
};

// デフォルトの書類データ（初回作成時に使用）
const defaultDocuments: DocumentsMap = {
  individual: [
    {
      document_type: "id_document_file",
      display_name: "身分証明書",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 1,
    },
    {
      document_type: "guarantor_docs_file",
      display_name: "保証人関連書類",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 2,
    },
    {
      document_type: "lease_contract_file",
      display_name: "賃貸借契約書",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 3,
    },
    {
      document_type: "income_proof_file",
      display_name: "収入証明書",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 4,
    },
  ],
  corporate: [
    {
      document_type: "lease_contract_file",
      display_name: "賃貸借契約書",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 1,
    },
    {
      document_type: "registry_cert_file",
      display_name: "登記事項証明書",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 2,
    },
    {
      document_type: "articles_file",
      display_name: "定款／会社概要",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 3,
    },
    {
      document_type: "seal_cert_file",
      display_name: "印鑑証明書",
      is_required: true,
      is_enabled: true,
      is_default: true,
      order: 4,
    },
  ],
};

export const fetchDocumentManagement =
  createAsyncThunk<
    DocumentManagementRecord | null,
    void,
    { rejectValue: ApiErrorPayload }
  >(
    "documentManagement/fetch",
    async (_, { rejectWithValue }) => {
      try {
        const res = await ipponApi.get(`${BaseURL}/api/v1/document-management`);
        const list = Array.isArray(res.data?.data) ? res.data.data : [];

        // データが存在する場合はそのまま返す
        if (list.length > 0) {
          return list[0] as DocumentManagementRecord;
        }

        // データが空の場合はデフォルトデータでPOSTして作成
        const createRes = await ipponApi.post(
          `${BaseURL}/api/v1/document-management`,
          { documents: defaultDocuments }
        );
        return createRes.data.data as DocumentManagementRecord;
      } catch (error: unknown) {
        return rejectWithValue(
          toApiErrorPayload(error, "Failed to fetch document management"),
        );
      }
    }
  );

export const createDocumentManagement = createAsyncThunk<
  DocumentManagementRecord,
  {
    documents: DocumentsMap;
    property_other_documents?: PropertyDocumentDefinition[];
  }
>(
  "documentManagement/create",
  async ({ documents, property_other_documents }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/document-management`,
        {
          documents,
          ...(property_other_documents !== undefined
            ? { property_other_documents }
            : {}),
        }
      );
      return res.data.data as DocumentManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to create document management"
      );
    }
  }
);

export const updateDocumentManagement = createAsyncThunk<
  DocumentManagementRecord,
  {
    document_management_id: string;
    documents: DocumentsMap;
    property_other_documents?: PropertyDocumentDefinition[];
  }
>(
  "documentManagement/update",
  async (
    { document_management_id, documents, property_other_documents },
    { rejectWithValue }
  ) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/document-management/${document_management_id}`,
        {
          documents,
          ...(property_other_documents !== undefined
            ? { property_other_documents }
            : {}),
        }
      );
      return res.data.data as DocumentManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update document management"
      );
    }
  }
);

export const upsertDocumentManagement = createAsyncThunk<
  DocumentManagementRecord,
  {
    existingId?: string | null;
    documents: DocumentsMap;
    property_other_documents?: PropertyDocumentDefinition[];
  }
>(
  "documentManagement/upsert",
  async (
    { existingId, documents, property_other_documents },
    { dispatch, rejectWithValue }
  ) => {
    try {
      if (existingId) {
        const resultAction: any = await dispatch(
          updateDocumentManagement({
            document_management_id: existingId,
            documents,
            property_other_documents,
          })
        );
        if (updateDocumentManagement.rejected.match(resultAction)) {
          throw resultAction.payload || new Error("update failed");
        }
        return resultAction.payload as DocumentManagementRecord;
      }
      const resultAction: any = await dispatch(
        createDocumentManagement({ documents, property_other_documents })
      );
      if (createDocumentManagement.rejected.match(resultAction)) {
        throw resultAction.payload || new Error("create failed");
      }
      return resultAction.payload as DocumentManagementRecord;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

type DocumentManagementState = {
  loading: boolean;
  error: ApiErrorPayload | null;
  record: DocumentManagementRecord | null;
};

const initialState: DocumentManagementState = {
  loading: false,
  error: null,
  record: null,
};

export const documentManagementSlice = createSlice({
  name: "documentManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDocumentManagement.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDocumentManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.record = action.payload as any;
    });
    builder.addCase(fetchDocumentManagement.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? null;
    });

    builder.addCase(createDocumentManagement.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createDocumentManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.record = action.payload as any;
    });
    builder.addCase(createDocumentManagement.rejected, (state) => {
      state.loading = false;
      state.error = null;
    });

    builder.addCase(updateDocumentManagement.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateDocumentManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.record = action.payload as any;
    });
    builder.addCase(updateDocumentManagement.rejected, (state) => {
      state.loading = false;
      state.error = null;
    });

    builder.addCase(upsertDocumentManagement.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(upsertDocumentManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.record = action.payload as any;
    });
    builder.addCase(upsertDocumentManagement.rejected, (state) => {
      state.loading = false;
      state.error = null;
    });
  },
});

export default documentManagementSlice.reducer;
